/**
 * Safe Toast Utility
 * Wraps react-toastify toast methods with error handling to prevent
 * "Cannot set properties of undefined (setting 'removalReason')" errors
 * Also prevents notification stacking by clearing previous toasts
 */

import { toast as reactToastify } from "react-toastify";

// Store active toast IDs to prevent closing undefined toasts
const activeToasts = new Set();
// Store toast IDs by type to allow clearing by type
const toastsByType = {
  success: new Set(),
  error: new Set(),
  warn: new Set(),
  info: new Set(),
};

// Configuration for auto-clearing previous toasts
let autoClearPrevious = true;
let maxToastsPerType = 3; // Maximum toasts of same type before clearing old ones

/**
 * Safe toast wrapper that handles errors gracefully
 */
const safeToast = {
  success: (message, options = {}) => {
    try {
      // Clear previous success toasts if auto-clear is enabled and limit reached
      if (autoClearPrevious && options.clearPrevious !== false) {
        if (toastsByType.success.size >= maxToastsPerType) {
          const toastsToClear = Array.from(toastsByType.success);
          toastsToClear.forEach(id => {
            try {
              reactToastify.dismiss(id);
              activeToasts.delete(id);
            } catch (e) {
              // Ignore dismiss errors
            }
          });
          toastsByType.success.clear();
        }
      }

      const toastId = reactToastify.success(message, {
        autoClose: options.autoClose !== undefined ? options.autoClose : 2000,
        ...options,
        onClose: () => {
          activeToasts.delete(toastId);
          toastsByType.success.delete(toastId);
          if (options.onClose) {
            try {
              options.onClose();
            } catch (error) {
              console.error("Error in toast onClose callback:", error);
            }
          }
        },
      });
      activeToasts.add(toastId);
      toastsByType.success.add(toastId);
      return toastId;
    } catch (error) {
      console.error("Toast error (success):", error);
      // Fallback to console if toast fails
      console.log("✓ Success:", message);
      return null;
    }
  },

  error: (message, options = {}) => {
    try {
      // Clear previous error toasts if auto-clear is enabled and limit reached
      if (autoClearPrevious && options.clearPrevious !== false) {
        if (toastsByType.error.size >= maxToastsPerType) {
          const toastsToClear = Array.from(toastsByType.error);
          toastsToClear.forEach(id => {
            try {
              reactToastify.dismiss(id);
              activeToasts.delete(id);
            } catch (e) {
              // Ignore dismiss errors
            }
          });
          toastsByType.error.clear();
        }
      }

      const toastId = reactToastify.error(message, {
        autoClose: options.autoClose !== undefined ? options.autoClose : 2000,
        ...options,
        onClose: () => {
          activeToasts.delete(toastId);
          toastsByType.error.delete(toastId);
          if (options.onClose) {
            try {
              options.onClose();
            } catch (error) {
              console.error("Error in toast onClose callback:", error);
            }
          }
        },
      });
      activeToasts.add(toastId);
      toastsByType.error.add(toastId);
      return toastId;
    } catch (error) {
      console.error("Toast error (error):", error);
      // Fallback to console if toast fails
      console.error("✗ Error:", message);
      return null;
    }
  },

  warn: (message, options = {}) => {
    try {
      // Clear previous warn toasts if auto-clear is enabled and limit reached
      if (autoClearPrevious && options.clearPrevious !== false) {
        if (toastsByType.warn.size >= maxToastsPerType) {
          const toastsToClear = Array.from(toastsByType.warn);
          toastsToClear.forEach(id => {
            try {
              reactToastify.dismiss(id);
              activeToasts.delete(id);
            } catch (e) {
              // Ignore dismiss errors
            }
          });
          toastsByType.warn.clear();
        }
      }

      const toastId = reactToastify.warn(message, {
        autoClose: options.autoClose !== undefined ? options.autoClose : 2000,
        ...options,
        onClose: () => {
          activeToasts.delete(toastId);
          toastsByType.warn.delete(toastId);
          if (options.onClose) {
            try {
              options.onClose();
            } catch (error) {
              console.error("Error in toast onClose callback:", error);
            }
          }
        },
      });
      activeToasts.add(toastId);
      toastsByType.warn.add(toastId);
      return toastId;
    } catch (error) {
      console.error("Toast error (warn):", error);
      // Fallback to console if toast fails
      console.warn("⚠ Warning:", message);
      return null;
    }
  },

  info: (message, options = {}) => {
    try {
      // Clear previous info toasts if auto-clear is enabled and limit reached
      if (autoClearPrevious && options.clearPrevious !== false) {
        if (toastsByType.info.size >= maxToastsPerType) {
          const toastsToClear = Array.from(toastsByType.info);
          toastsToClear.forEach(id => {
            try {
              reactToastify.dismiss(id);
              activeToasts.delete(id);
            } catch (e) {
              // Ignore dismiss errors
            }
          });
          toastsByType.info.clear();
        }
      }

      const toastId = reactToastify.info(message, {
        autoClose: options.autoClose !== undefined ? options.autoClose : 2000,
        ...options,
        onClose: () => {
          activeToasts.delete(toastId);
          toastsByType.info.delete(toastId);
          if (options.onClose) {
            try {
              options.onClose();
            } catch (error) {
              console.error("Error in toast onClose callback:", error);
            }
          }
        },
      });
      activeToasts.add(toastId);
      toastsByType.info.add(toastId);
      return toastId;
    } catch (error) {
      console.error("Toast error (info):", error);
      // Fallback to console if toast fails
      console.info("ℹ Info:", message);
      return null;
    }
  },

  dismiss: (toastId) => {
    try {
      if (toastId && activeToasts.has(toastId)) {
        reactToastify.dismiss(toastId);
        activeToasts.delete(toastId);
        // Remove from type tracking
        Object.values(toastsByType).forEach(set => set.delete(toastId));
      }
    } catch (error) {
      console.error("Error dismissing toast:", error);
    }
  },

  clear: () => {
    try {
      reactToastify.dismiss();
      activeToasts.clear();
      Object.values(toastsByType).forEach(set => set.clear());
    } catch (error) {
      console.error("Error clearing toasts:", error);
    }
  },

  clearByType: (type) => {
    try {
      if (toastsByType[type]) {
        const toastsToClear = Array.from(toastsByType[type]);
        toastsToClear.forEach(id => {
          try {
            reactToastify.dismiss(id);
            activeToasts.delete(id);
          } catch (e) {
            // Ignore dismiss errors
          }
        });
        toastsByType[type].clear();
      }
    } catch (error) {
      console.error("Error clearing toasts by type:", error);
    }
  },

  // Configuration methods
  setAutoClear: (enabled) => {
    autoClearPrevious = enabled;
  },

  setMaxToastsPerType: (max) => {
    maxToastsPerType = max;
  },
};

export default safeToast;

